import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/AuthContext';
import { supabase } from '@/lib/supabase';
import { financialAPI } from '@/services/api';
import { useQuery } from '@tanstack/react-query';
import { TrendingUp, Users, Wallet, Copy, CheckCheck } from 'lucide-react';
import StatsCard from '@/components/dashboard/StatsCard';
import ProfitabilityClock from '@/components/dashboard/ProfitabilityClock';
import QuickActions from '@/components/dashboard/QuickActions';
import RecentTransactions from '@/components/dashboard/RecentTransactions';
import NetworkMembersTable from '@/components/dashboard/NetworkMembersTable';
import { formatCurrency, getCommissionRate } from '@/lib/planConfig';
import { toast } from 'sonner';

export default function Dashboard() {
  const { user, isAuthenticated, isLoadingAuth } = useAuth();
  const navigate = useNavigate();
  const [copied, setCopied] = React.useState(false);

  // Redirect para login se não autenticado
  React.useEffect(() => {
    if (!isLoadingAuth && !isAuthenticated) {
      console.log('Dashboard: Usuário não autenticado, redirecionando para login...');
      navigate('/login', { replace: true });
    }
  }, [isLoadingAuth, isAuthenticated, navigate]);

  // Não renderiza nada enquanto carrega ou se não autenticado
  if (isLoadingAuth || !isAuthenticated) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gold"></div>
      </div>
    );
  }

  React.useEffect(() => {
    if (!user?.id) return;

    const processNewUser = async () => {
      const isNew = localStorage.getItem('just_registered') === 'true';
      const refCode = localStorage.getItem('referral_code');

      if (isNew) {
        localStorage.removeItem('just_registered');
        toast.success(t('dashboard.welcomeMessage'), { duration: 6000 });
      }

      // Always try to link referral if code exists (even if just_registered was lost)
      if (refCode) {
        localStorage.removeItem('referral_code');
        try {
          // TODO: Implementar função de vinculação de indicação com Supabase
          console.log('Referral code to link:', refCode);
        } catch (e) {
          console.error('Erro ao vincular indicação:', e);
        }
      }
    };

    processNewUser().catch(console.error);
  }, [user?.id, isLoadingAuth, isAuthenticated]);

  const { data: investments = [] } = useQuery({
    queryKey: ['investments', user?.id],
    queryFn: async () => {
      try {
        const data = await financialAPI.getInvestments({ status: 'active' });
        return data.investments || [];
      } catch (error) {
        console.error('Error fetching investments:', error);
        return [];
      }
    },
    enabled: !!user?.id,
  });

  const { data: networkMembers = [] } = useQuery({
    queryKey: ['network', user?.id],
    queryFn: async () => {
      try {
        const data = await financialAPI.getNetwork();
        return data.network || [];
      } catch (error) {
        console.error('Error fetching network:', error);
        return [];
      }
    },
    enabled: !!user?.id,
  });

  const { data: transactions = [], isLoading: isLoadingTransactions } = useQuery({
    queryKey: ['transactions', user?.id],
    queryFn: async () => {
      try {
        const response = await financialAPI.getTransactions({ limit: 20 });
        console.log('🔍 Dashboard - Transações retornadas:', response);
        
        // A API pode retornar {transactions: [...]} ou [...]
        const txs = response?.transactions || response || [];
        console.log('🔍 Dashboard - Processando transações:', txs?.length || 0, 'itens');
        
        return Array.isArray(txs) ? txs.slice(0, 10) : [];
      } catch (error) {
        console.error('❌ Error fetching transactions:', error);
        return [];
      }
    },
    enabled: !!user?.id,
    refetchOnMount: 'always',
    staleTime: 0,
  });

  const { data: balanceData = {} } = useQuery({
    queryKey: ['balance', user?.id],
    queryFn: async () => {
      try {
        return await financialAPI.getBalance();
      } catch (error) {
        console.error('Error fetching balance:', error);
        return {};
      }
    },
    enabled: !!user?.id,
    refetchOnMount: 'always',
    staleTime: 0,
  });

  const networkMemberIds = (Array.isArray(networkMembers) ? networkMembers : []).map((m) => m.referred_id).filter(Boolean);

  const { data: networkData = {} } = useQuery({
    queryKey: ['network-investments', networkMemberIds.join(',')],
    queryFn: async () => {
      const data = await financialAPI.getNetwork();
      return data;
    },
    enabled: networkMemberIds.length > 0,
  });
  
  const networkInvestments = networkData.indirectInvestments || {};

  // Criar mapa de investimento por usuário (networkInvestments já é um objeto {userId: amount})
  const investmentByUser = networkInvestments;

  // Calcular totais reais a partir dos investimentos
  const totalInvested = investments.reduce((sum, i) => sum + (parseFloat(i.amount) || 0), 0);
  const totalEarnings = investments.reduce((sum, i) => sum + (parseFloat(i.total_yield) || 0), 0);

  const availableBalance = balanceData?.available_balance || 0;

  const activeInvestment = investments.find((i) => i.status === 'active');
  
  const networkEarnings = (Array.isArray(networkMembers) ? networkMembers : []).reduce((sum, m) => {
    const inv = investmentByUser[m.referred_id];
    const pct = getCommissionRate(m.level);
    return sum + (inv?.total_yield || 0) * pct;
  }, 0);
  const totalValue = availableBalance;

  const referralLink = user?.referral_code
    ? `${import.meta.env.VITE_APP_URL || window.location.origin}/register?ref=${user.referral_code}`
    : '';

  const handleCopyLink = () => {
    if (referralLink) {
      navigator.clipboard.writeText(referralLink);
      setCopied(true);
      toast.success('Link copied to clipboard!');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-foreground">
          Hello, <span className="gold-text">{user?.full_name?.split(' ')[0] || 'User'}</span>
        </h1>
        <p className="text-sm text-muted-foreground mt-1">Welcome to your investment dashboard</p>
      </div>

      {/* Referral link */}
      {referralLink && (
        <div className="flex items-center gap-2 p-3 rounded-xl border border-gold/20 bg-gold/5">
          <span className="text-xs text-muted-foreground flex-shrink-0">Your referral link:</span>
          <span className="text-xs text-gold truncate flex-1">{referralLink}</span>
          <button onClick={handleCopyLink} className="flex-shrink-0 p-1.5 rounded-md hover:bg-gold/10 transition">
            {copied ? <CheckCheck className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4 text-gold" />}
          </button>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatsCard title="Invested" value={totalInvested} icon={TrendingUp} color="blue" subtitle="" />
        <StatsCard title="Earnings" value={totalEarnings} icon={TrendingUp} color="green" subtitle="" />
        <StatsCard title="Network Earnings" value={networkEarnings} icon={Users} color="purple" subtitle="" />
        <StatsCard title="Available Balance" value={availableBalance} icon={Wallet} color="amber" subtitle="" />
      </div>

      {/* Quick Actions */}
      <QuickActions />

      {/* Main content */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ProfitabilityClock investment={activeInvestment} />
        <RecentTransactions transactions={transactions} />
      </div>

      {/* Network Members Table */}
      <NetworkMembersTable />
    </div>
  );
}